/**
 * InstructionsTab — the final section instruction the drafter actually used.
 *
 * Renders the instruction markdown for one section in document order.
 * Non-conditional prose renders as-is. Every conditional block is preceded
 * by a condition header (the plain-English condition + verdict chip), and
 * the block's CONTENT renders only when the block was actually served:
 *   - TRUE       → "Passed" chip, content rendered
 *   - FALSE      → "Failed" chip, header ONLY — no content (the block was
 *                  not served to the drafter)
 *   - UNKNOWN /
 *     UNRESOLVED → "Included (unverified)" chip, content rendered (the
 *                  conservative include DID serve it to the drafter)
 *
 * Data: getSectionInstructions(runId, moduleId) — persisted per module run
 * (ModuleRun.instructions_used). Runs recorded before instruction capture
 * existed return available:false → friendly empty state. Fetch-on-open with
 * the DraftTab cancellation-guard pattern; no SSE.
 */

import { Spinner, makeStyles, mergeClasses } from "@fluentui/react-components";
import type React from "react";
import { useEffect, useRef, useState } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import {
  type InstructionSegment,
  type InstructionsUsedPayload,
  getSectionInstructions,
  saveCustomSectionInstruction,
} from "../../../api/client";
import type { SpineSection } from "../../hooks/useVariantCRun";
import { brandColors, fonts, inkColors, statusColors, surfaceColors, typeScale } from "../../tokens";
import { assistantMarkdownRules } from "../markdownStyles";

interface InstructionsTabProps {
  section: SpineSection;
  runId: string;
  /** True when this run is a custom submission (built on the writer's own Word
   *  document). Custom sections have no doctype-template instruction, so the tab
   *  becomes an EDITOR for a per-section instruction the writer authors instead
   *  of a read-only view of the template chunk. */
  isCustomSubmission?: boolean;
  /** Called after a custom instruction is saved/cleared so the shell can update
   *  the spine row (which gates the section's Generate / Draft-more buttons). */
  onInstructionSaved?: (sectionId: string, instruction: string | null) => void;
}

// ---------------------------------------------------------------------------
// Render model — pure, exported for tests
// ---------------------------------------------------------------------------

export type InstructionRenderStatus = "text" | "passed" | "excluded" | "unverified";

export interface InstructionRenderItem {
  key: string;
  status: InstructionRenderStatus;
  condition: string | null;
  content: string;
}

/** Map persisted segments to render items. Empty-content segments are
 *  dropped (marker-only gaps); verdict vocabulary degrades safely — any
 *  unknown verdict renders as "unverified" (included), never "excluded". */
export function deriveInstructionRenderModel(payload: InstructionsUsedPayload | null): InstructionRenderItem[] {
  if (!payload) return [];
  const items: InstructionRenderItem[] = [];
  payload.segments.forEach((seg: InstructionSegment, i: number) => {
    if (!seg.content || !seg.content.trim()) return;
    let status: InstructionRenderStatus;
    if (seg.kind !== "conditional") {
      status = "text";
    } else if (seg.verdict === "TRUE") {
      status = "passed";
    } else if (seg.verdict === "FALSE") {
      status = "excluded";
    } else {
      status = "unverified";
    }
    items.push({
      key: `seg-${i}`,
      status,
      condition: seg.kind === "conditional" ? seg.condition : null,
      content: seg.content,
    });
  });
  return items;
}

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------

const useStyles = makeStyles({
  root: {
    padding: "12px 16px",
    overflowY: "auto",
    height: "100%",
  },
  meta: {
    fontSize: typeScale.code,
    color: inkColors.ink3,
    fontFamily: fonts.body,
    marginBottom: "10px",
  },
  markdown: assistantMarkdownRules,
  conditionalBlock: {
    margin: "10px 0",
    paddingLeft: "10px",
    borderLeftWidth: "3px",
    borderLeftStyle: "solid",
    borderLeftColor: surfaceColors.lineStrong,
  },
  blockPassed: { borderLeftColor: statusColors.cool },
  blockExcluded: { borderLeftColor: statusColors.danger },
  blockUnverified: { borderLeftColor: surfaceColors.lineStrong },
  blockHeader: {
    display: "flex",
    alignItems: "baseline",
    columnGap: "8px",
    marginBottom: "4px",
    flexWrap: "wrap",
  },
  conditionText: {
    fontSize: typeScale.code,
    color: inkColors.ink2,
    fontFamily: fonts.body,
    fontStyle: "italic",
  },
  chip: {
    fontSize: "10px",
    fontFamily: fonts.body,
    fontWeight: 700,
    textTransform: "uppercase",
    letterSpacing: "0.4px",
    padding: "1px 7px",
    borderRadius: "8px",
    whiteSpace: "nowrap",
  },
  chipPassed: {
    color: statusColors.cool,
    background: "rgba(38, 223, 155, 0.12)",
  },
  chipExcluded: {
    color: statusColors.danger,
    background: "rgba(225, 29, 72, 0.10)",
  },
  chipUnverified: {
    color: inkColors.ink3,
    background: surfaceColors.paperDim,
  },
  conditionLabel: {
    fontSize: typeScale.code,
    color: inkColors.ink3,
    fontFamily: fonts.body,
    fontWeight: 700,
    textTransform: "uppercase",
    letterSpacing: "0.4px",
    whiteSpace: "nowrap",
  },
  centered: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    rowGap: "8px",
    padding: "32px 16px",
    color: inkColors.ink3,
    fontSize: typeScale.body,
    fontFamily: fonts.body,
    textAlign: "center",
  },
  errorText: {
    color: statusColors.danger,
  },
  // -- Custom-submission instruction editor --------------------------------
  editorRoot: {
    padding: "12px 16px",
    display: "flex",
    flexDirection: "column",
    rowGap: "10px",
    height: "100%",
    boxSizing: "border-box",
  },
  editorIntro: {
    fontSize: typeScale.code,
    color: inkColors.ink3,
    fontFamily: fonts.body,
    lineHeight: 1.5,
  },
  editorTextarea: {
    width: "100%",
    minHeight: "160px",
    resize: "vertical",
    boxSizing: "border-box",
    padding: "10px 12px",
    fontFamily: fonts.body,
    fontSize: typeScale.body,
    color: inkColors.ink,
    background: surfaceColors.paper,
    border: `1px solid ${surfaceColors.lineStrong}`,
    borderRadius: "8px",
    lineHeight: 1.5,
    "::placeholder": { color: inkColors.ink4 },
    ":focus": {
      outlineStyle: "none",
      border: `1px solid ${brandColors.primary}`,
    },
  },
  editorActions: {
    display: "flex",
    alignItems: "center",
    columnGap: "8px",
  },
  editorButton: {
    fontFamily: fonts.body,
    fontSize: typeScale.body,
    fontWeight: 700,
    padding: "6px 14px",
    borderRadius: "8px",
    border: "1px solid transparent",
    cursor: "pointer",
  },
  editorSave: {
    background: brandColors.primary,
    border: `1px solid ${brandColors.primary}`,
    color: "var(--on-brand)",
    ":disabled": {
      opacity: 0.45,
      cursor: "not-allowed",
    },
  },
  editorClear: {
    background: "transparent",
    border: `1px solid ${surfaceColors.lineStrong}`,
    color: inkColors.ink2,
    ":disabled": {
      opacity: 0.45,
      cursor: "not-allowed",
    },
  },
  editorStatus: {
    fontSize: typeScale.code,
    fontFamily: fonts.body,
    color: inkColors.ink3,
  },
});

const CHIP_LABEL: Record<Exclude<InstructionRenderStatus, "text">, string> = {
  passed: "Passed",
  excluded: "Failed",
  unverified: "Included (unverified)",
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// Custom-submission editor
// ---------------------------------------------------------------------------

/** Editor for a CUSTOM section's per-section drafting instruction. Custom
 *  sections are the writer's own document headings with no template chunk to
 *  show, so this replaces the read-only view with a place to say what the
 *  section should contain — the text that will drive its draft. */
const CustomInstructionEditor: React.FC<{
  section: SpineSection;
  runId: string;
  onInstructionSaved?: (sectionId: string, instruction: string | null) => void;
}> = ({ section, runId, onInstructionSaved }) => {
  const styles = useStyles();
  const saved = section.instruction ?? "";
  const [text, setText] = useState<string>(saved);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Reseed when the writer switches to a different section (the tab is reused)
  // or when a save reflects the new instruction back onto the spine row.
  // biome-ignore lint/correctness/useExhaustiveDependencies: section.id/instruction are the intended reseed triggers; setText/setError are stable setState.
  useEffect(() => {
    setText(section.instruction ?? "");
    setError(null);
  }, [section.id, section.instruction]);

  const trimmed = text.trim();
  const dirty = trimmed !== saved.trim();

  const persist = async (value: string) => {
    setSaving(true);
    setError(null);
    try {
      const resp = await saveCustomSectionInstruction(runId, section.id, value);
      onInstructionSaved?.(section.id, resp.instruction);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className={styles.editorRoot}>
      <div className={styles.editorIntro}>
        This is a custom submission, so this section has no template instruction. Tell the drafting agent what{" "}
        <strong>{section.title || "this section"}</strong> should contain — it drafts from your source library using
        this instruction. A section can only be drafted once it has an instruction saved.
      </div>
      <textarea
        className={styles.editorTextarea}
        value={text}
        placeholder="e.g. Summarise the study population and key baseline demographics, drawing figures from the sources."
        onChange={(e) => setText(e.target.value)}
        disabled={saving}
        aria-label={`Drafting instruction for ${section.title || section.id}`}
      />
      <div className={styles.editorActions}>
        <button
          type="button"
          className={mergeClasses(styles.editorButton, styles.editorSave)}
          disabled={saving || !dirty || trimmed.length === 0}
          onClick={() => void persist(trimmed)}
        >
          {saving ? "Saving…" : saved ? "Update instruction" : "Save instruction"}
        </button>
        {saved && (
          <button
            type="button"
            className={mergeClasses(styles.editorButton, styles.editorClear)}
            disabled={saving}
            onClick={() => {
              setText("");
              void persist("");
            }}
          >
            Clear
          </button>
        )}
        {error ? (
          <span className={mergeClasses(styles.editorStatus, styles.errorText)}>{error}</span>
        ) : saved && !dirty ? (
          <span className={styles.editorStatus}>Saved</span>
        ) : null}
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const InstructionsTab: React.FC<InstructionsTabProps> = ({
  section,
  runId,
  isCustomSubmission,
  onInstructionSaved,
}) => {
  // Custom submission: the section is the writer's own heading with no template
  // instruction — show the editor for the per-section drafting instruction.
  // Standard run: the read-only view of the template chunk the drafter used.
  if (isCustomSubmission) {
    return <CustomInstructionEditor section={section} runId={runId} onInstructionSaved={onInstructionSaved} />;
  }
  return <TemplateInstructionsView section={section} runId={runId} />;
};

// ---------------------------------------------------------------------------
// Template (standard-run) read-only view
// ---------------------------------------------------------------------------

/** The read-only view of the template instruction the drafter used — the
 *  original behaviour for standard (non-custom) runs. Split into its own
 *  component so ``InstructionsTab`` can branch to the custom editor without
 *  calling these hooks conditionally. */
const TemplateInstructionsView: React.FC<{ section: SpineSection; runId: string }> = ({ section, runId }) => {
  const styles = useStyles();
  const [payload, setPayload] = useState<InstructionsUsedPayload | null>(null);
  const [available, setAvailable] = useState<boolean | null>(null);
  const [notDrafted, setNotDrafted] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const requestKeyRef = useRef<string>("");

  useEffect(() => {
    let cancelled = false;
    const key = `${runId}:${section.id}`;
    requestKeyRef.current = key;
    setLoading(true);
    setError(null);
    setNotDrafted(false);
    setPayload(null);
    setAvailable(null);
    (async () => {
      try {
        const resp = await getSectionInstructions(runId, section.id);
        if (cancelled || requestKeyRef.current !== key) return;
        setAvailable(resp.available);
        setPayload(resp.instructions_used);
      } catch (err) {
        if (cancelled || requestKeyRef.current !== key) return;
        // A 404 means this section has no record yet — it simply hasn't been
        // drafted. That's an expected empty state, not a failure, so route it
        // to the calm "unavailable" message rather than the red error banner.
        if ((err as { status?: number }).status === 404) {
          setNotDrafted(true);
        } else {
          setError(err instanceof Error ? err.message : String(err));
        }
      } finally {
        if (!cancelled && requestKeyRef.current === key) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [runId, section.id]);

  if (loading) {
    return (
      <div className={styles.centered}>
        <Spinner size="tiny" />
        <span>Loading instructions…</span>
      </div>
    );
  }
  if (notDrafted) {
    return (
      <div className={styles.centered}>
        <span>Instructions aren’t available yet because this section hasn’t been drafted.</span>
        <span>Draft the section to see the exact instruction text used.</span>
      </div>
    );
  }
  if (error) {
    return (
      <div className={styles.centered}>
        <span className={styles.errorText}>Could not load instructions: {error}</span>
      </div>
    );
  }
  if (!available || !payload) {
    return (
      <div className={styles.centered}>
        <span>Instructions were not recorded for this run.</span>
        <span>
          Sections drafted after the instruction-capture rollout will show the exact instruction text used, including
          which conditional blocks passed or failed.
        </span>
      </div>
    );
  }

  const items = deriveInstructionRenderModel(payload);
  const excludedCount = items.filter((i) => i.status === "excluded").length;

  return (
    <div className={styles.root}>
      <div className={styles.meta}>
        {payload.instruction_section ? `Instruction §${payload.instruction_section}` : "Module instructions"}
        {payload.module_activation
          ? ` · module condition: "${payload.module_activation.condition}" → ${payload.module_activation.verdict}`
          : ""}
        {excludedCount > 0 ? ` · ${excludedCount} condition${excludedCount === 1 ? "" : "s"} failed` : ""}
      </div>
      {items.map((item) => {
        if (item.status === "text") {
          return (
            <div key={item.key} className={styles.markdown}>
              <ReactMarkdown remarkPlugins={[remarkGfm]}>{item.content}</ReactMarkdown>
            </div>
          );
        }
        const blockClass = mergeClasses(
          styles.conditionalBlock,
          item.status === "passed" && styles.blockPassed,
          item.status === "excluded" && styles.blockExcluded,
          item.status === "unverified" && styles.blockUnverified,
        );
        const chipClass = mergeClasses(
          styles.chip,
          item.status === "passed" && styles.chipPassed,
          item.status === "excluded" && styles.chipExcluded,
          item.status === "unverified" && styles.chipUnverified,
        );
        return (
          <div key={item.key} className={blockClass}>
            <div className={styles.blockHeader}>
              <span className={styles.conditionLabel}>Condition</span>
              {item.condition && <span className={styles.conditionText}>{item.condition}</span>}
              <span className={chipClass}>{CHIP_LABEL[item.status]}</span>
            </div>
            {/* Failed conditions show the header ONLY — the block was not
                served to the drafter, so its content is not displayed. */}
            {item.status !== "excluded" && (
              <div className={styles.markdown}>
                <ReactMarkdown remarkPlugins={[remarkGfm]}>{item.content}</ReactMarkdown>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default InstructionsTab;
